import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Info } from "lucide-react";

/**
 * Aviso/alerta padronizado — triângulo VERMELHO pra problema (danger/warning),
 * check VERDE pra "tudo certo" (success). Substitui os boxes de erro
 * laranja-sutis que "não chamavam atenção" (feedback da reunião Julio).
 */
export type NoticeTone = "danger" | "warning" | "success" | "info";

const TONES: Record<
  NoticeTone,
  { bg: string; border: string; chipBg: string; iconColor: string; Icon: typeof AlertTriangle }
> = {
  danger: { bg: "#fff0ee", border: "#ff5577", chipBg: "rgba(255,85,119,0.16)", iconColor: "#d92d4a", Icon: AlertTriangle },
  warning: { bg: "#fff8ef", border: "var(--nd-orange)", chipBg: "rgba(255,112,0,0.14)", iconColor: "var(--nd-orange-pun)", Icon: AlertTriangle },
  success: { bg: "#eefaf0", border: "#3aa657", chipBg: "rgba(58,166,87,0.16)", iconColor: "#2e8b48", Icon: CheckCircle2 },
  info: { bg: "var(--wf-paper)", border: "var(--wf-ink-4)", chipBg: "var(--wf-ink-6, #f3efe5)", iconColor: "var(--wf-ink-2)", Icon: Info },
};

export function Notice({
  tone = "warning",
  title,
  children,
  style,
}: {
  tone?: NoticeTone;
  title?: string;
  children?: ReactNode;
  style?: React.CSSProperties;
}) {
  const t = TONES[tone];
  const Icon = t.Icon;
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        padding: "14px 16px",
        background: t.bg,
        border: `1px solid ${t.border}`,
        borderRadius: 10,
        fontFamily: "var(--wf-font-body)",
        ...style,
      }}
    >
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: t.chipBg,
          color: t.iconColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={17} strokeWidth={2.25} />
      </span>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        {title && <span style={{ fontSize: 13, fontWeight: 700, color: "var(--wf-ink)" }}>{title}</span>}
        {children != null && (
          <span style={{ fontSize: 12.5, color: "var(--wf-ink-2)", lineHeight: 1.5, wordBreak: "break-word" }}>
            {children}
          </span>
        )}
      </div>
    </div>
  );
}
